import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { CircleSlash, Clock, Map, Receipt, Undo2, UserCheck } from 'lucide-react'
import { env } from '@/lib/env'
import { endOfDayParam, formatDateTimeCompact, startOfDayParam } from '@/lib/datetime'
import { fetchAllPages, type ExportColumn } from '@/lib/export'
import { humanizeEnum, truncate } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import {
  PAYMENT_METHODS,
  PAYMENT_VERIFICATION_STATUSES,
  REQUEST_PAYMENT_STATUSES,
  SERVICE_TYPES,
  TRIP_REQUEST_STATUSES,
  type AdminTripRequest,
  type TripRequestFilters,
} from '@/types/api'
import { useTableState } from '@/hooks/useTableState'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { DataTable } from '@/components/ui/DataTable'
import { ExportMenu } from '@/components/ui/ExportMenu'
import { FilterBar, FilterDate, FilterSelect } from '@/components/ui/FilterBar'
import { Pagination } from '@/components/ui/Pagination'
import { RowActions, StackedCell } from '@/components/ui/RowActions'
import { SearchInput } from '@/components/ui/SearchInput'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { RefundDialog } from '@/features/refunds/RefundDialog'
import { PaymentAmountCell, PaymentMethodCell } from '@/features/payments/PaymentCell'
import { PaymentDetailsDialog } from '@/features/payments/PaymentDetailsDialog'
import { describePaymentMethod, describeVerificationStatus } from '@/features/payments/describe'
import { EditScheduleDialog, type ScheduleTarget } from '@/features/scheduling/EditScheduleDialog'
import { AreaSurchargeBadge, LateChargeBadge } from '@/features/area-pricing/AreaSurchargeBadge'
import { describeAreaSurcharge, hasAreaChips, lateChargeExportValue } from '@/features/area-pricing/describe'
import { AddOnBadges } from '@/features/trips/AddOnBadges'
import { describeAddOns } from '@/features/trips/addOns'
import { DueTripsAlert } from '@/features/trips/DueTripsAlert'
import { RouteMapDialog } from '@/features/maps/RouteMapDialog'
import { toRouteTarget, type RouteTarget } from '@/features/maps/geo'
import { AssignDriverDialog } from './AssignDriverDialog'
import { CancelBookingDialog } from './CancelBookingDialog'
import { fetchTripRequests } from './api'

const DEFAULT_FILTERS = {
  search: '',
  status: '',
  serviceType: '',
  paymentStatus: '',
  paymentMethod: '',
  verificationStatus: '',
  from: '',
  to: '',
}

export function TripRequestsPage() {
  const table = useTableState({ defaultSort: 'createdAt', defaultFilters: DEFAULT_FILTERS })
  const [assigning, setAssigning] = useState<AdminTripRequest | null>(null)
  const [cancelling, setCancelling] = useState<number | null>(null)
  const [refunding, setRefunding] = useState<AdminTripRequest | null>(null)
  const [rescheduling, setRescheduling] = useState<ScheduleTarget | null>(null)
  const [viewingPayment, setViewingPayment] = useState<AdminTripRequest | null>(null)
  const [viewingRoute, setViewingRoute] = useState<RouteTarget | null>(null)

  useEffect(() => {
    document.title = `Trip requests · ${env.appName} Operations`
  }, [])

  const filters = useMemo<TripRequestFilters>(
    () => ({
      page: table.page,
      size: table.size,
      sort: table.sort,
      direction: table.direction,
      search: table.filters.search || undefined,
      status: table.filters.status || undefined,
      serviceType: table.filters.serviceType || undefined,
      paymentStatus: table.filters.paymentStatus || undefined,
      paymentMethod: table.filters.paymentMethod || undefined,
      verificationStatus: table.filters.verificationStatus || undefined,
      from: table.filters.from ? startOfDayParam(table.filters.from) : undefined,
      to: table.filters.to ? endOfDayParam(table.filters.to) : undefined,
    }),
    [table.page, table.size, table.sort, table.direction, table.filters],
  )

  const requestsQuery = useQuery({
    queryKey: queryKeys.tripRequests.list(filters),
    queryFn: () => fetchTripRequests(filters),
    placeholderData: (previous) => previous,
  })

  const columns = useMemo<ColumnDef<AdminTripRequest, unknown>[]>(
    () => [
      {
        id: 'bookingReference',
        header: 'Reference',
        meta: { sortKey: 'bookingReference', mobileLabel: 'Reference' },
        cell: ({ row }) => (
          <StackedCell
            primary={
              <span style={{ fontFamily: 'var(--font-mono)' }}>{row.original.bookingReference ?? `#${row.original.id}`}</span>
            }
            secondary={formatDateTimeCompact(row.original.createdAt)}
          />
        ),
      },
      {
        // The pickup time the passenger picked when booking — distinct from the
        // request's own `createdAt`, which the Reference cell already shows. The
        // end time is derived from the package duration and is null on TRAVEL
        // bookings, which are open-ended by design.
        id: 'startTime',
        header: 'Trip time',
        meta: { sortKey: 'startTime', mobileLabel: 'Trip time' },
        cell: ({ row }) => (
          <StackedCell
            primary={formatDateTimeCompact(row.original.startTime)}
            secondary={
              row.original.endTime ? `ends ${formatDateTimeCompact(row.original.endTime, '—')}` : 'open-ended'
            }
          />
        ),
      },
      {
        id: 'userName',
        header: 'Passenger',
        meta: { sortKey: 'userName', mobileLabel: 'Passenger' },
        cell: ({ row }) => (
          <StackedCell primary={row.original.userName ?? '—'} secondary={row.original.userPhone ?? undefined} />
        ),
      },
      {
        id: 'route',
        header: 'Route',
        meta: { mobileLabel: 'Route' },
        cell: ({ row }) => (
          <StackedCell
            primary={truncate(row.original.pickupAddress, 38)}
            secondary={`→ ${truncate(row.original.dropoffAddress, 38, 'No drop-off set')}`}
          />
        ),
      },
      {
        id: 'packageName',
        header: 'Package',
        meta: { sortKey: 'packageName', mobileLabel: 'Package', hideOnMobile: true },
        cell: ({ row }) => (
          <StackedCell
            primary={row.original.packageName ?? '—'}
            secondary={
              <>
                {[row.original.categoryName, humanizeEnum(row.original.serviceType, '')]
                  .filter(Boolean)
                  .join(' · ')}
                {/*
                  A round trip, or one with hours at the coast, is a different
                  dispatch from the one-way route the package name describes —
                  the driver is out twice as long. Shown with the package, where
                  the operator reads what was bought, rather than buried in the
                  amount.
                */}
                {row.original.addOns.any ? (
                  <span style={{ display: 'block', marginTop: 2 }}>
                    <AddOnBadges addOns={row.original.addOns} />
                  </span>
                ) : null}
                {/* Which Cairo groups the two ends are in, and whether either
                    was late at night, when that cost something — inside the
                    fare, which is why it sits with what was bought rather than
                    in the amount. */}
                {hasAreaChips(row.original.areaSurcharge) ? (
                  <span style={{ display: 'block', marginTop: 2 }}>
                    <AreaSurchargeBadge area={row.original.areaSurcharge} />{' '}
                    <LateChargeBadge area={row.original.areaSurcharge} />
                  </span>
                ) : null}
              </>
            }
          />
        ),
      },
      {
        // No sortKey: sorting maps to a server-side sort param, and car model
        // is not one the requests endpoint accepts.
        id: 'carModel',
        header: 'Car',
        meta: { mobileLabel: 'Car', hideOnMobile: true },
        cell: ({ row }) => <StackedCell primary={truncate(row.original.carModel, 28)} />,
      },
      {
        id: 'driverName',
        header: 'Driver',
        meta: { sortKey: 'driverName', mobileLabel: 'Driver' },
        cell: ({ row }) =>
          row.original.driverName ? (
            <StackedCell primary={row.original.driverName} secondary={row.original.driverPhone ?? undefined} />
          ) : (
            <span style={{ color: 'var(--text-tertiary)' }}>Not assigned</span>
          ),
      },
      {
        // The fare and the split it was paid by, in one column. `basePrice`
        // stays the sort key because that is what the server can order by; the
        // total shown is the fare plus any cancellation charge, which is the
        // amount that was actually charged.
        id: 'basePrice',
        header: 'Amount',
        meta: { sortKey: 'basePrice', mobileLabel: 'Amount', align: 'right', hideOnMobile: true },
        cell: ({ row }) => <PaymentAmountCell payment={row.original.payment} />,
      },
      {
        id: 'paymentMethod',
        header: 'Payment',
        meta: { mobileLabel: 'Payment' },
        cell: ({ row }) => <PaymentMethodCell payment={row.original.payment} />,
      },
      {
        id: 'paymentStatus',
        header: 'Paid',
        meta: { sortKey: 'paymentStatus', mobileLabel: 'Paid', hideOnMobile: true },
        cell: ({ row }) => <StatusBadge status={row.original.payment.paymentStatus} />,
      },
      {
        id: 'status',
        header: 'Status',
        meta: { sortKey: 'status', mobileLabel: 'Status' },
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        meta: { align: 'right', sticky: true, width: '340px' },
        cell: ({ row }) => {
          // Only a pending request can be approved; anything else already has
          // its trip, and the API would refuse a second one.
          const pending = row.original.status === 'PENDING'
          // Server-computed, and true for a confirmed booking too. Cancelling
          // used to be offered on pending rows alone, which left the bookings
          // customers actually ring up about — the ones with a driver already
          // on the way — with no way out of the dashboard.
          const cancellable = row.original.canCancel
          const reschedulable = row.original.canModifySchedule
          // `refundCandidate` is computed by the server. The dialog then re-reads
          // full eligibility before anything can be submitted.
          const refundable = row.original.refundCandidate

          return (
            <RowActions>
              {/*
                Before approving, the useful question about a booking is where
                it actually goes — which driver is near it, and whether the
                drop-off matches the package that was bought. Two truncated
                addresses in the Route column do not answer either.
              */}
              <Button
                size="sm"
                variant="subtle"
                iconLeft={<Map aria-hidden="true" />}
                onClick={() => setViewingRoute(toRouteTarget(row.original))}
              >
                Route
              </Button>
              <Button
                size="sm"
                variant="subtle"
                iconLeft={<Receipt aria-hidden="true" />}
                onClick={() => setViewingPayment(row.original)}
              >
                Payment
              </Button>
              {pending ? (
                /*
                  Still clickable when the payment blocks it. A disabled button
                  would say "no" and nothing else, whereas the dialog behind this
                  names the reason and points at the fix — which for a dead card
                  payment is switching it to Instapay, and for an Instapay
                  booking is verifying the transfer. The tone drops to secondary
                  so the queue still reads at a glance: the primary buttons are
                  the rows that are actually ready to go out.
                */
                <Button
                  size="sm"
                  variant={row.original.assignmentBlockedReason ? 'secondary' : 'primary'}
                  iconLeft={<UserCheck aria-hidden="true" />}
                  title={row.original.assignmentBlockedReason ?? undefined}
                  onClick={() => setAssigning(row.original)}
                >
                  Approve
                </Button>
              ) : null}
              {reschedulable ? (
                <Button
                  size="sm"
                  variant="subtle"
                  iconLeft={<Clock aria-hidden="true" />}
                  onClick={() =>
                    setRescheduling({
                      kind: 'request',
                      id: row.original.id,
                      reference: row.original.bookingReference,
                      customerName: row.original.userName,
                      startTime: row.original.startTime,
                      endTime: row.original.endTime,
                    })
                  }
                >
                  Times
                </Button>
              ) : null}
              {cancellable ? (
                <Button
                  size="sm"
                  variant="secondary"
                  iconLeft={<CircleSlash aria-hidden="true" />}
                  onClick={() => setCancelling(row.original.id)}
                >
                  {/*
                    "Reject" is what turning down a request in the queue is
                    called; once it is a confirmed booking with a driver, the
                    same action is a cancellation, and the dialog behind it
                    talks about refunds and penalties either way.
                  */}
                  {pending ? 'Reject' : 'Cancel'}
                </Button>
              ) : null}
              {refundable ? (
                <Button
                  size="sm"
                  variant="secondary"
                  iconLeft={<Undo2 aria-hidden="true" />}
                  onClick={() => setRefunding(row.original)}
                >
                  Refund
                </Button>
              ) : null}
            </RowActions>
          )
        },
      },
    ],
    [],
  )

  const exportColumns = useMemo<ExportColumn<AdminTripRequest>[]>(
    () => [
      { header: 'ID', value: (row) => row.id },
      { header: 'Reference', value: (row) => row.bookingReference ?? '' },
      { header: 'Created', value: (row) => formatDateTimeCompact(row.createdAt, '') },
      { header: 'Passenger', value: (row) => row.userName ?? '' },
      { header: 'Passenger phone', value: (row) => row.userPhone ?? '' },
      { header: 'Driver', value: (row) => row.driverName ?? '' },
      { header: 'Category', value: (row) => row.categoryName ?? '' },
      { header: 'Package', value: (row) => row.packageName ?? '' },
      { header: 'Add-ons', value: (row) => describeAddOns(row.addOns) },
      { header: 'Add-ons amount', value: (row) => (row.addOns.any ? row.addOns.totalAmount : '') },
      { header: 'Area surcharge', value: (row) => describeAreaSurcharge(row.areaSurcharge) },
      {
        header: 'Area surcharge amount',
        value: (row) => (row.areaSurcharge.any ? row.areaSurcharge.total : ''),
      },
      { header: 'Late-night charge', value: (row) => lateChargeExportValue(row.areaSurcharge) },
      { header: 'Service type', value: (row) => row.serviceType ?? '' },
      { header: 'Car model', value: (row) => row.carModel ?? '' },
      { header: 'Pickup', value: (row) => row.pickupAddress ?? '' },
      { header: 'Drop-off', value: (row) => row.dropoffAddress ?? '' },
      { header: 'Scheduled start', value: (row) => formatDateTimeCompact(row.startTime, '') },
      { header: 'Fare', value: (row) => row.basePrice ?? '' },
      { header: 'Total charged', value: (row) => row.payment.totalAmount },
      { header: 'Paid with points', value: (row) => row.payment.pointsUsed },
      { header: 'Paid externally', value: (row) => row.payment.externalAmount },
      { header: 'Payment method', value: (row) => row.payment.paymentMethodCode ?? row.paymentMethod ?? '' },
      { header: 'Payment status', value: (row) => row.paymentStatus ?? '' },
      { header: 'Verification', value: (row) => row.payment.verificationStatus ?? '' },
      { header: 'Verified by', value: (row) => row.payment.verifiedByAdminName ?? '' },
      { header: 'Verified at', value: (row) => formatDateTimeCompact(row.payment.verifiedAt, '') },
      { header: 'Proof of payment', value: (row) => (row.payment.hasProof ? 'Yes' : 'No') },
      { header: 'Blocked from assignment', value: (row) => row.assignmentBlockedReason ?? '' },
      { header: 'Status', value: (row) => row.status ?? '' },
    ],
    [],
  )

  const page = requestsQuery.data
  const pendingCount = page?.content.filter((request) => request.status === 'PENDING').length ?? 0

  return (
    <>
      {/*
        Raised before anything else on the screen: a trip whose start or end
        time has passed while its status stayed put is the one thing here that
        gets worse the longer it waits, and this queue is where the supervisors
        who would fix it actually sit.
      */}
      <DueTripsAlert />

      <PageHeader
        title="Trip requests"
        description="The approval queue. Approving a request assigns a driver and creates the trip; cancelling one — pending or already confirmed — asks what should happen to its money. Times can be changed here when a customer calls."
        meta={
          page ? (
            <>
              <span>{page.totalElements} matching requests</span>
              {pendingCount > 0 ? <span>{pendingCount} pending on this page</span> : null}
            </>
          ) : null
        }
      />

      <Card flush>
        <FilterBar
          search={
            <SearchInput
              value={table.filters.search}
              onChange={(value) => table.setFilter('search', value)}
              placeholder="Search reference, passenger, driver or address…"
              label="Search trip requests"
            />
          }
          actions={
            <ExportMenu
              columns={exportColumns}
              baseName="drive-for-me-trip-requests"
              title="Drive For Me — Trip requests"
              fetchRows={() =>
                fetchAllPages((exportPage, exportSize) =>
                  fetchTripRequests({ ...filters, page: exportPage, size: exportSize }),
                )
              }
            />
          }
          activeFilterCount={table.activeFilterCount}
          onReset={table.resetFilters}
        >
          <FilterSelect
            label="Status"
            value={table.filters.status}
            onChange={(value) => table.setFilter('status', value)}
            options={TRIP_REQUEST_STATUSES.map((status) => ({ value: status, label: humanizeEnum(status) }))}
          />
          <FilterSelect
            label="Service"
            value={table.filters.serviceType}
            onChange={(value) => table.setFilter('serviceType', value)}
            options={SERVICE_TYPES.map((type) => ({ value: type, label: humanizeEnum(type) }))}
          />
          <FilterSelect
            label="Payment"
            value={table.filters.paymentStatus}
            onChange={(value) => table.setFilter('paymentStatus', value)}
            options={REQUEST_PAYMENT_STATUSES.map((status) => ({ value: status, label: humanizeEnum(status) }))}
          />
          <FilterSelect
            label="Method"
            value={table.filters.paymentMethod}
            onChange={(value) => table.setFilter('paymentMethod', value)}
            options={PAYMENT_METHODS.map((method) => ({
              value: method,
              label: describePaymentMethod({ paymentMethodCode: method, paymentMethod: method }),
            }))}
          />
          <FilterSelect
            label="Verification"
            value={table.filters.verificationStatus}
            onChange={(value) => table.setFilter('verificationStatus', value)}
            options={PAYMENT_VERIFICATION_STATUSES.map((status) => ({
              value: status,
              label: describeVerificationStatus(status),
            }))}
          />
          <FilterDate
            label="From"
            value={table.filters.from}
            onChange={(value) => table.setFilter('from', value)}
            max={table.filters.to || undefined}
          />
          <FilterDate
            label="To"
            value={table.filters.to}
            onChange={(value) => table.setFilter('to', value)}
            min={table.filters.from || undefined}
          />
        </FilterBar>

        <DataTable
          caption="Drive For Me trip requests"
          data={page?.content ?? []}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={requestsQuery.isLoading}
          isFetching={requestsQuery.isFetching && !requestsQuery.isLoading}
          error={requestsQuery.isError ? requestsQuery.error : undefined}
          onRetry={() => void requestsQuery.refetch()}
          sort={table.sort}
          direction={table.direction}
          onSortChange={table.setSort}
          emptyTitle={table.activeFilterCount > 0 ? 'No requests match these filters' : 'No trip requests yet'}
          emptyDescription={
            table.activeFilterCount > 0
              ? 'Try a different status or widen the date range.'
              : 'Requests appear here as passengers book through the app.'
          }
          emptyAction={
            table.activeFilterCount > 0 ? (
              <Button variant="secondary" onClick={table.resetFilters}>
                Clear filters
              </Button>
            ) : null
          }
        />

        {page && page.totalElements > 0 ? (
          <Pagination
            page={page.page}
            size={page.size}
            totalElements={page.totalElements}
            totalPages={page.totalPages}
            onPageChange={table.setPage}
            onSizeChange={table.setSize}
            noun="request"
            disabled={requestsQuery.isFetching}
          />
        ) : null}
      </Card>

      <AssignDriverDialog request={assigning} onClose={() => setAssigning(null)} />

      <RouteMapDialog target={viewingRoute} onClose={() => setViewingRoute(null)} />

      <RefundDialog tripRequestId={refunding?.id ?? null} onClose={() => setRefunding(null)} />

      <PaymentDetailsDialog
        tripRequestId={viewingPayment?.id ?? null}
        bookingReference={viewingPayment?.bookingReference ?? null}
        payment={viewingPayment?.payment ?? null}
        onClose={() => setViewingPayment(null)}
      />

      <EditScheduleDialog target={rescheduling} onClose={() => setRescheduling(null)} />

      {/*
        This used to be a ConfirmDialog whose only copy was a guess at what
        would happen to the money — "a card refund will be started
        automatically where one applies", inferred from a payment-method
        string. The backend no longer refunds in full by default, and the
        operator now decides, so the dialog asks rather than predicts.
      */}
      <CancelBookingDialog tripRequestId={cancelling} onClose={() => setCancelling(null)} />
    </>
  )
}
